/**
 * Content for /mvp-validator, from the "MVP Validator (Coming Soon)" artboard.
 *
 * ── EVERYTHING HERE IS FUTURE TENSE, AND THAT IS WHY IT IS SAFE ─────────────
 *
 * The product does not exist. The artboard is written accordingly — "what the
 * report WILL answer", "how it WILL work", "we are still building it" — so
 * there is no claim about anything that has happened. That is the opposite of
 * the case-studies problem and the reason this page could be built without a
 * ruling on its copy.
 *
 * ── THE ONE OMISSION ────────────────────────────────────────────────────────
 *
 * The artboard has a logo wall: "Built for the founders, accelerators and
 * venture studios already using UXAuditPro", over Northgate, Vessel, Marketly,
 * Apiarium and Datalyte.
 *
 * It is omitted. Five named companies are asserted as customers, which is the
 * same fabrication the three case studies were deleted for on 2026-08-18 — and
 * here it also contradicts the page it sits on, which says in four other places
 * that the product is not built yet. Nobody can already be using it.
 *
 * To exist it needs five real customers who have agreed in writing to be named.
 */

/** The hero. Copy preserved from the live page; the badge is new. */
export const HERO = {
  badge: 'AI MVP Validator',
  badgeNote: 'Coming soon',
  h1: 'Know if your startup idea will work before you build it.',
  sub: 'Get a comprehensive market validation report with AI research and human expertise. Clear GO/PIVOT/NO-GO verdict in 48 hours.',
  note: 'No spam. One email when early access opens.',
  /* The three figures under the form. "Q4 2026" is labelled TARGET launch, so
   * it is a stated plan rather than a promise about the past — the same
   * distinction that makes the rest of this page safe. */
  stats: [
    { value: 'Q4 2026', label: 'target launch' },
    { value: '6', label: 'validation dimensions' },
    { value: '1', label: 'clear verdict' },
  ],
};

/**
 * PRESERVED FROM THE LIVE PAGE, not from the artboard. These four cards and
 * their heading are existing content and content preservation keeps them, even
 * though the artboard reframes the same ground as four questions below. The
 * overlap is deliberate and is flagged to the owner rather than resolved by
 * deleting one of them.
 */
export const WHAT_YOU_GET = {
  eyebrow: 'What You Get',
  heading: 'Everything you need to validate',
  items: [
    { title: 'Market Analysis', desc: 'TAM/SAM/SOM sizing with data sources' },
    { title: 'Competitor Intel', desc: 'Deep dive into top 10 competitors' },
    { title: 'Customer Evidence', desc: 'Real signals from Reddit, forums, reviews' },
    { title: 'Financial Model', desc: '3-year projections with scenarios' },
  ],
};

export const QUESTIONS = {
  heading: 'What the Report Will Answer',
  lead: 'Four questions decide whether an idea is worth building. Each one will be researched, evidenced and scored.',
  items: [
    { q: 'Is anyone looking for this?', a: 'Search demand, community discussion and existing spend, gathered as evidence rather than opinion.' },
    { q: 'Who already solves it?', a: 'A competitor map with positioning, pricing and the gaps their customers complain about.' },
    { q: 'Will they pay for it?', a: 'Pricing benchmarks and willingness-to-pay signals for the segment you are targeting.' },
    { q: 'What should you do next?', a: 'A GO, PIVOT or NO-GO verdict with the specific first move it implies for your idea.' },
  ],
};

export const HOW_IT_WILL_WORK = {
  heading: 'How It Will Work',
  lead: 'No workshops, no discovery calls. Describe the idea and read the verdict two days later. This is the flow we are building towards.',
  steps: [
    { when: '10 minutes', title: 'Describe the idea', desc: 'Answer a short brief: the problem, who has it, how you plan to charge. A link or deck is enough if you have one.' },
    { when: 'AI + human', title: 'Research and review', desc: 'AI gathers market, competitor and pricing evidence. A human reviewer checks every claim and cuts the noise.' },
    { when: '48 hours', title: 'Get the verdict', desc: 'A full validation report with scores, sources and a GO, PIVOT or NO-GO call you can act on the same week.' },
  ],
};

export const INSIDE_REPORT = {
  heading: 'Inside the Report',
  lead: 'Twelve to twenty pages, written to be forwarded. Every section will end with what it means for your build decision.',
  rows: [
    { title: 'Executive verdict', pages: '1 page' },
    { title: 'Market demand evidence', pages: '4 pages' },
    { title: 'Competitor map', pages: '3 pages' },
    { title: 'Pricing & willingness to pay', pages: '2 pages' },
    { title: 'Risks and open questions', pages: '2 pages' },
    { title: 'Recommended next move', pages: '1 page' },
  ],
};

/**
 * A DESIGN PREVIEW OF A HYPOTHETICAL IDEA, labelled as such on the page.
 *
 * The label is what makes this honest: it is a picture of a report format for
 * an obviously invented example ("subscription tracker for indie run clubs"),
 * not a claim that this validation was run. The artboard labels it the same way
 * and that label is not decoration — it is the thing that stops it being a
 * fabricated result. Do not remove it.
 */
export const PREVIEW = {
  label: 'Design preview',
  idea: 'Subscription tracker for indie run clubs',
  verdict: 'GO',
  verdictNote: 'Enough demand and pricing headroom to justify building a narrow first version.',
  score: 74,
  /* The artboard's own six dimensions and values, transcribed rather than
   * invented. `strong` picks the accent fill over the soft one, exactly as the
   * artboard does — it is not a threshold, it is which bars it drew hot. */
  dimensions: [
    { name: 'Market demand', value: 82, strong: true },
    { name: 'Competition', value: 54, strong: false },
    { name: 'Willingness to pay', value: 76, strong: true },
    { name: 'Reachable audience', value: 68, strong: false },
    { name: 'Differentiation', value: 61, strong: false },
    { name: 'Time to first revenue', value: 71, strong: false },
  ],
  metrics: [
    { label: 'Competitors reviewed', value: '14' },
    { label: 'Sources cited', value: '37' },
    { label: 'Delivered in', value: '41h' },
  ],
  summary: 'Demand is real but thin at the club level, and the two incumbents both price for gyms rather than volunteer organisers.',
  signals: [
    { label: 'Strongest signal', body: 'Organisers already pay for two tools to cover one workflow.' },
    { label: 'Biggest risk', body: 'Seasonal churn: half of clubs go dormant outside race season.' },
  ],
  footnotes: ['37 sources cited', '14 competitors', 'PDF · 18 pages'],
};

export const FAQS = [
  { q: 'When does the MVP Validator launch?', a: 'We are still building it. Everything on this page describes the product we are working towards. Join the waitlist and you will hear the moment early access opens.' },
  { q: 'What if the verdict is NO-GO?', a: 'You save the months you would have spent building it. A NO-GO report explains which assumption failed and what would have to change for the answer to be different.' },
  { q: 'Do I need a product or prototype first?', a: 'No. The validation runs on the idea itself, which is the point of doing it before you build. If you already have a landing page or a deck, send it and it becomes part of the evidence.' },
  { q: 'Is my idea kept confidential?', a: 'Your brief and report are private to your account, never used to train models, and never shared as an example without written permission.' },
];

export const CLOSING = {
  heading: 'Be First in Line',
  lead: 'The MVP Validator is in development. Leave your email and we will invite you as soon as early access opens.',
};
